/** Hook to handle things element list */
import { graphHooks } from '@tunasong/graph-lib/react'
import { logger, type Invite } from '@tunasong/models'
import { useSelector } from '@tunasong/redux'
import type { ACL, Entity, ItemOperation, Persisted } from '@tunasong/schemas'
import { getEntityPath, useAlert, useNavigate } from '@tunasong/ui-lib'
import { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { ShareDialog } from './share-dialog.js'

export const useShare = (entity?: Persisted<Entity>, onClose?: () => void) => {
  const { shareEntity, shareEntityStatus } = graphHooks.useEntityShare()
  const { isLoading } = shareEntityStatus
  const [invite] = graphHooks.useInvite()
  const navigate = useNavigate()
  const { alert } = useAlert()

  const userId = useSelector(state => state.user.userId)
  const [showShare, setShowShare] = useState(false)
  const show = () => setShowShare(true)
  const hide = () => {
    setShowShare(false)
    if (onClose) {
      onClose()
    }
  }
  const handleShare = (acls: ACL[]) => {
    invariant(entity, 'Cannot share an undefined entity')

    // Detect a self-leave: a non-owner removing their own ACL from a shared entity.
    // In that case we navigate the user away — the entity will become inaccessible to them.
    const isOwner = entity.userId === userId
    const wasShared = (entity.acls ?? []).some(a => a.principal === userId)
    const stillShared = acls.some(a => a.principal === userId)
    const isLeaving = !isOwner && wasShared && !stillShared

    /** Share will recursively share all children as well */
    void shareEntity({ entity, acls }).then(() => {
      if (!isLeaving) {
        return
      }
      hide()
      alert({
        severity: 'success',
        title: 'You left',
        message: entity.name ? `You no longer have access to ${entity.name}.` : `You no longer have access.`,
        duration: 4000,
      })
      void navigate('/app')
    })
  }

  const inviteInProgress = useRef(false)
  const handleInvite = (email: string, allow: ItemOperation) => {
    if (!(userId && entity?.id)) {
      return
    }

    if (inviteInProgress.current) {
      logger.warn(`Invite already in progress for ${email}, skipping duplicate invite`)
      return
    }
    inviteInProgress.current = true

    /** The /invite endpoint will handle invites */

    const targetPath = `${getEntityPath({ entity })}?mode=signup`

    const params: Invite = {
      inviteeEmail: email,
      inviterId: userId,
      entityIds: [entity.id],
      allow,
      targetPath,
    }

    void invite({ invite: params })
      .unwrap()
      .finally(() => {
        inviteInProgress.current = false
      })
  }

  const dialog = entity ? (
    <ShareDialog
      key={`${entity.id}-share-dialog`}
      open={showShare}
      entity={entity}
      onShare={handleShare}
      onInvite={handleInvite}
      onClose={hide}
      isLoading={isLoading}
    />
  ) : null

  return { share: handleShare, invite: handleInvite, show, hide, dialog, isLoading }
}
